import * as React from 'react'
import { connect } from 'react-redux'
import { Badge, badges, Icon, IconType, Packshot } from 'DVWebNodeUI'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { IDetailPageState } from '../../reducers'
import { sanitizedRequestContextSelector, IRequestContext } from '../../selectors/requestContext'
import * as st from './carouselCollectionItem.module.scss'
import { buildDetailPageUrl } from '../../utils/url'
import { isDisrupted } from '../../utils/liveState'
import { safeBoolean } from '../../utils/utils'
import { SrcSize } from '../../utils/imageUtils'
import { ResponsiveImage } from '../images/responsiveImage'
import { isHoverableTitle, isMovie } from '../../utils/titleTypes'
import { DETAIL_FRAGMENT } from '../../reducers/detail'


const PACKSHOT_PHONE_LANDSCAPE_HEIGHT = 150
const PACKSHOT_DEFAULT_HEIGHT = 200
const SIXTEEN_NINE_DEFAULT_WIDTH = 356
const SIXTEEN_NINE_MOBILE_WIDTH = 266
const ENABLE_HOVER_CLASS = "dv-universal-hover-enabled"
const SIMS_CARD_REFTAG_PREFIX = "atv_dp_sims_c_"

interface OwnProps {
  idx: number
  titleId: string
  refTagPrefix?: string
}

interface StateProps {
  detail: WpsRuntime.atv.wps.DetailFragmentModelProps
  requestContext: IRequestContext
  pageContext: WpsRuntime.atv.wps.PageContextModelProps
  pageLink?: string
  useSixteenByNine?: boolean
}

type Props = StateProps & OwnProps

const EmptyFallbackPackshot = ({
  url,
  titleId,
  useSixteenByNine
}: {
  url: string
  titleId?: string
  useSixteenByNine?: boolean
}) => (
  <div className={`${useSixteenByNine ? st.avFallbackPackshotSixteenNine : st.avFallbackPackshot} ${ENABLE_HOVER_CLASS}`} data-asin={titleId}>
    <a href={url}>
        <Icon use={IconType.SMILE} />
    </a>
  </div>
)

const FloatingBadges = ({
  detail
}: {
  detail: WpsRuntime.atv.wps.DetailFragmentModelProps
}) => {
  const isLive = detail.liveState && safeBoolean(detail.liveState.isLive)
  const text = detail.liveState && detail.liveState.text
  const liveStateId = detail.liveState && detail.liveState.id
  const dateTimeBadge = detail.dateTimeBadge
  const badgeType = isLive ? badges.LIVE : badges.CRITICAL

  return (<div className={st.avPackshotBadges}>
    {
      (isDisrupted(liveStateId) || isLive) ?
        text && <span className={st.avPackshotBadge}>
          <Badge use={badgeType}>{text}</Badge>
        </span> :
        dateTimeBadge && <span className={st.avPackshotBadge}>
          <Badge use={badges.WHITE}>{dateTimeBadge}</Badge>
        </span>
    }
  </div>)
}

const getWidthByHeightAndRatio = (height: number, ratio: number) => Math.round(height * ratio)

const ConnectedCarouselCollectionItem = ({ detail, titleId, idx, requestContext, pageContext, pageLink, refTagPrefix, useSixteenByNine }: Props) => {
  const isRetail = requestContext.isRetail

  // Falling back to titleId just to be safe in case Swift fail to return asin/gti

  const detailTitleId = (isRetail ? detail.asin : detail.compactGti) || titleId
  const hoverTitleId = (isRetail ? detail.asin : detail.gti) || isHoverableTitle(titleId) ? titleId : undefined
  const refTag = refTagPrefix ? `${refTagPrefix}${idx + 1}` : `${SIMS_CARD_REFTAG_PREFIX}${idx + 1}`
  const ratio = isMovie(detail.titleType) ? 0.75 : 1.335
  const detailUrl = buildDetailPageUrl(
    {
      parameters: requestContext.parameters,
      originalURI: requestContext.originalURI,
      isRetail
    }, detailTitleId, refTag)

  const pageUrl = pageLink || detailUrl

  // 16 x 9 experiment as part of cast and crew
  let imageUrl, imageProps;
  if (useSixteenByNine) {
    imageUrl = detail.images && detail.images.covershot
    imageProps = {
      alt: detail.title,
      src: imageUrl,
      baseValue: SIXTEEN_NINE_DEFAULT_WIDTH,
      srcSizes: [
        { 'max-phone-landscape': SIXTEEN_NINE_MOBILE_WIDTH },
      ] as SrcSize[],
      skipStripTags: true // tags for 16x9 added in wps to blur background for fallback
    }
  } else {
    imageUrl = detail.images && detail.images.titleshot
    imageProps = {
      alt: detail.title,
      src: imageUrl,
      baseValue: getWidthByHeightAndRatio(PACKSHOT_DEFAULT_HEIGHT, ratio),
      srcSizes: [
        {'max-phone-landscape': getWidthByHeightAndRatio(PACKSHOT_PHONE_LANDSCAPE_HEIGHT, ratio)},
      ] as SrcSize[]
    }
  }

  const packshotProps = {
    url: pageUrl,
    isPrime: isRetail && safeBoolean(detail.isPrime) // We only show prime badge on Retail
  }

  const enableHoverClass = hoverTitleId && pageContext.enableHover ? ENABLE_HOVER_CLASS : undefined

  return <div className={st.avSimsPackshot}>
    {imageUrl && <Packshot {...packshotProps} className={`${enableHoverClass}`} asin={hoverTitleId} >
      <ResponsiveImage {...imageProps} />
    </Packshot>}
    {!imageUrl && <EmptyFallbackPackshot url={pageUrl} titleId={hoverTitleId}/>}
    <FloatingBadges detail={detail}/>
  </div>
}

const mapStateToProps = (
  state: IDetailPageState,
  ownProps: OwnProps
): StateProps => ({
  detail: state.detail[DETAIL_FRAGMENT][ownProps.titleId]!,
  requestContext: sanitizedRequestContextSelector(state),
  pageContext: state.pageContext,
  pageLink: state.pageLink[ownProps.titleId],
  useSixteenByNine: state.features.useSixteenByNine
})

export const CarouselCollectionItem = connect(mapStateToProps)(ConnectedCarouselCollectionItem)
